import React, {
  useEffect, useState, useRef, useContext,
} from 'react';

// components
import Loader from '../../juristec-ui/core/Loader';
// import InstanceTable from '../../juristec-ui/core/InstanceTable';
import AdminToolbar from '../../components/Toolbars/AdminToolbar';
import InstanceUserTable from '../../juristec-ui/core/InstanceUserTable';
import NewInstance from '../../components/ProgressiveModals/NewInstance';
import AddUser from '../../components/Modals/AddUser';
import AddAntecipei from '../../components/Modals/AddAntecipei';
import OnboardingReport from '../../components/Modals/OnboardingReport';
import TransferUser from '../../components/Modals/TransferUser';
import ChangeUserRole from '../../components/Modals/ChangeUserRole';
import ReportsTable from '../../components/ReportsTable';

import InstanceTable from '../../components/InstanceTable';
import FileReport from '../../components/FilesView/FileReport';

// providers
import InstanceProvider, { InstanceContext } from '../../context/InstanceProvider';
import { AlertContext } from '../../context/AlertProvider';
import { FilesContext } from '../../context/FilesProvider';
import { ModalContext } from '../../context/ModalProvider';
import { AuthContext } from '../../context/AuthProvider';
import { verifyFileSheetType } from '../../juristec-ui/utils/validators/fileValidators';
import AddBenner from '../../components/Modals/AddBenner/AddBenner';

const ROLES = {
  SCIENTIST: 'scientist',
  GUEST: 'guest',
  SUPER: 'super',
};

function AdminPage() {
  const [filterText, setTxtFilter] = useState('');
  const [tab, setTab] = useState('customers');

  const { state: instanceState, instanceAPI } = useContext(InstanceContext);
  const { user, claimsUser } = useContext(AuthContext);
  const { setAlertConfig } = useContext(AlertContext);
  const { filesAPI } = useContext(FilesContext);
  const { setModalConfig, toggleModal, closeModal } = useContext(ModalContext);

  const isMounted = useRef(false);
  const cronRef = useRef(null);

  const handleText = (e) => {
    const { value } = e.target;
    clearTimeout(cronRef.current);
    cronRef.current = setTimeout(() => {
      setTxtFilter(value);
    }, 200);
  };

  useEffect(() => {
    setTxtFilter('');
  }, [tab]);

  // USUÁRIOS DA INSTÂNCIA
  async function handleGetInstanceUser(instance) {
    let users = [];
    if (!instance.users) {
      const res = await instanceAPI.getInstanceUsers(instance.id);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
        });
        return null;
      }
      users = res.users;
    } else {
      users = instance.users;
    }
    return users;
  }

  /**
   * Transfers all allowed data from one user to another
   * @param {array} usersValids List of allowed users to receive the transfer
   * @param {object} selectedUser User information to be transferred
   * @param {object} instance User instance info
   */
  const handleTransferUser = async (usersValids, selectedUser, instance) => {
    const options = usersValids.reduce((aux, u) => {
      if ((u.role === ROLES.SUPER || u.role === ROLES.SCIENTIST) && u.uid !== selectedUser.uid) {
        aux.push({ label: u.name, value: u.uid, id: `transfer-${u.uid}` });
      }
      return aux;
    }, []);

    const hideModal = () => toggleModal('transfer-user');

    /** Submit the transfer request */
    const submitTransfer = async (userToReceive) => {
      const res = await instanceAPI.transferUserData(
        instance.id, selectedUser.uid, userToReceive.value,
      );
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }
      setAlertConfig({
        type: 'success',
        text: 'Usuário transferido com sucesso!',
        child: `Arquivos e dashboards do usuário ${selectedUser.name} foram transferidos para o usuário ${userToReceive.label}`,
      });
      hideModal();
    };

    setModalConfig({
      title: 'Transferir usuário',
      nodeTarget: 'transfer-user',
      children: (
        <TransferUser
          selectedUser={selectedUser}
          usersOptions={options}
          close={hideModal}
          submit={submitTransfer}
        />
      ),
    });
  };

  /**
   * @async Allows the admin to change the roles of any user on any instance
   * @param {array} users array of users in an instance
   * @param {object} selectedUser user data to be changed
   * @param {object} instance selected users instance
   */
  const handleChangePermission = async (users, selectedUser, instance) => {
    const usersQtd = { super: 0, scientist: 0, guest: 0 };

    /**
     * @type {array} Counts and filters users based on their roles,
     * and creates a array of option
     */
    const validScientists = users.reduce((aux, u) => {
      if (u.role !== 'guest') {
        if (u.role === 'super') usersQtd.super += 1;
        else usersQtd.scientist += 1;
        if (u.uid !== selectedUser.uid) aux.push({ id: u.uid, label: u.name, value: u.uid });
      } else usersQtd.guest += 1;
      return aux;
    }, []);

    const hideModal = () => toggleModal('change-role');

    /**
     * Checks if the role change can be made
     * @param {string} role the new role
     * @returns {boolean} the verification result
     */
    const checkLimit = (role) => {
      switch (role) {
        case 'super':
          if (usersQtd.super >= instance.superS) {
            setAlertConfig({
              type: 'error',
              text: 'Limite máximo de usuários proprietários atingido nesta instância',
            });
            return false;
          }
          break;
        case 'scientist':
          if (usersQtd.scientist >= instance.scientists) {
            setAlertConfig({
              type: 'error',
              text: 'Limite máximo usuários cientistas atingido nesta instância',
            });
            return false;
          }
          if (selectedUser.role === 'super' && usersQtd.super === 1) {
            setAlertConfig({
              type: 'error',
              text: 'Instância deve ter no mínimo 1 proprietário',
            });
            return false;
          }
          break;
        case 'guest':
          if (usersQtd.guest >= instance.guests) {
            setAlertConfig({
              type: 'error',
              text: 'Limite máximo de usuários convidados atingido nesta instância',
            });
            return false;
          }
          if (selectedUser.role === 'super' && usersQtd.super === 1) {
            setAlertConfig({
              type: 'error',
              text: 'Instância deve ter no mínimo 1 proprietário',
            });
            return false;
          }
          break;
        default:
          break;
      }
      return true;
    };

    /**
     * @async Send the request with the modified user data
     * @param {string} newRole the user new role
     * @param {string} scientistKey the user new responsible scientist.
     * Only used when his role is 'guest'
     */
    const handleSubmitRole = async (newRole, scientistKey) => {
      if (!checkLimit(newRole)) return;
      const res = await instanceAPI.changeUserRoleByAdmin(
        instance.id, scientistKey, selectedUser.email, newRole,
      );
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }
      setAlertConfig({
        type: 'success',
        text: `Permissão do usuário ${selectedUser.name} alterada com sucesso!`,
      });
      instanceAPI.getInstanceUsers(instance.id);
      hideModal();
      toggleModal();
    };

    setModalConfig({
      title: 'Alterar permissão',
      nodeTarget: 'change-role',
      children: (
        <ChangeUserRole
          selectedUser={selectedUser}
          scientists={validScientists}
          close={hideModal}
          submit={handleSubmitRole}
        />
      ),
    });
  };

  const handleBlockUser = (selectedUser) => {
    const handler = async (set) => {
      const res = await instanceAPI.blockUser(
        selectedUser.instanceId, selectedUser.scientist, selectedUser.email, selectedUser.disabled ? 'false' : 'True',
      );
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
        });
        // set(false);
        return;
      }

      setAlertConfig({
        type: 'success',
        text: `Usuário ${selectedUser.disabled ? 'desbloqueado' : 'bloqueado'} com sucesso!`,
      });
      set(!selectedUser.disabled);
    };

    const modalHandler = (f) => {
      setAlertConfig({
        type: 'warning',
        text: `Deseja ${selectedUser.disabled ? 'desbloquear' : 'bloquear'} usuário?`,
        child: selectedUser.disabled
          ? `O usuário ${selectedUser.email} voltará a acessar o sistema!`
          : `O usuário ${selectedUser.email} não poderá acessar o sistema!`,
        withoutConfirm: true,
        withFunction: true,
        confirmFunction: f,
      });
    };

    return { modalHandler, handler };
  };

  const handleDeleteUser = (selectedUser) => {
    const handler = async (f) => {
      const res = await instanceAPI.deleteUser(selectedUser.instanceId, selectedUser.email);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Usuário apagado com sucesso!',
      });
      f();
    };

    const modalHandler = (f) => {
      setAlertConfig({
        type: 'warning',
        text: 'Deseja apagar o usuário?',
        withoutConfirm: true,
        withFunction: true,
        confirmFunction: f,
      });
    };

    return { handler, modalHandler };
  };

  const openGuestsModal = async (instance) => {
    // fazer fetch somente se n tem os usuários ainda.
    const users = await handleGetInstanceUser(instance);
    if (!users) return;
    const guests = users.filter((u) => u.role === ROLES.GUEST);
    const handlePermission = (selected) => handleChangePermission(users, selected, instance);

    setModalConfig({
      title: 'Convidados',
      children: (
        <InstanceUserTable
          users={guests}
          userRole="convidado"
          handleBlock={handleBlockUser}
          handleDelete={handleDeleteUser}
          handlePermission={handlePermission}
          closeModal={closeModal}
        />
      ),
    });
  };

  const openSupersModal = async (instance) => {
    // fazer fetch somente se n tem os usuários ainda.
    const users = await handleGetInstanceUser(instance);
    if (!users) return;

    const supers = users.filter((u) => u.role === ROLES.SUPER);
    const transferModal = (selected) => { handleTransferUser(users, selected, instance); };
    const handlePermission = (selected) => handleChangePermission(users, selected, instance);

    setModalConfig({
      title: 'Proprietários',
      children: (
        <InstanceUserTable
          users={supers}
          userRole="super"
          handleTransfer={transferModal}
          handleBlock={handleBlockUser}
          handleDelete={handleDeleteUser}
          handlePermission={handlePermission}
          closeModal={closeModal}
        />
      ),
    });
  };

  const openScientistModal = async (instance) => {
    // fazer fetch somente se n tem os usuários ainda.
    const users = await handleGetInstanceUser(instance);
    if (!users) return;

    const scientists = users.filter((u) => u.role === ROLES.SCIENTIST);
    const transferModal = (selected) => { handleTransferUser(users, selected, instance); };
    const handlePermission = (selected) => handleChangePermission(users, selected, instance);

    setModalConfig({
      title: 'Cientistas',
      children: (
        <InstanceUserTable
          users={scientists}
          userRole="cientista"
          handleTransfer={transferModal}
          handleBlock={handleBlockUser}
          handleDelete={handleDeleteUser}
          handlePermission={handlePermission}
          closeModal={closeModal}
        />
      ),
    });
  };

  // INSTÂNCIA
  const handleNewInstance = async () => {
    const handleSubmit = async (instanceConfigs, emailsSuper, emailsScientist, image) => {
      const res = await instanceAPI.createInstance(
        instanceConfigs, emailsSuper, emailsScientist, image,
      );
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: `Instância ${instanceConfigs.name} criada com sucesso!`,
      });
      closeModal();
      instanceAPI.getInstances();
    };

    setModalConfig({
      title: 'Nova Instância',
      disableFocus: true,
      children: (
        <NewInstance
          closeModal={closeModal}
          submitNewInstance={handleSubmit}
        />
      ),
    });
  };

  const handleEditInstance = async (instance) => {
    const handleSubmit = async (instanceConfigs) => {
      const res = await instanceAPI.editInstance(instanceConfigs);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: `Instância ${instanceConfigs.name} editado com sucesso!`,
      });
      closeModal();
      instanceAPI.getInstances();
    };

    setModalConfig({
      title: 'Editar Instância',
      disableFocus: true,
      children: (
        <NewInstance
          isEdit
          toEditInstance={instance}
          submitEditInstance={handleSubmit}
          closeModal={closeModal}
        />
      ),
    });
  };

  const handleBlockInstance = async (instance) => {
    const handleBlock = async () => {
      const res = await instanceAPI.blockInstance(instance.id, !instance.disabled);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: `${instance.disabled ? 'Desbloqueado' : 'Bloqueado'} com sucesso!`,
      });
    };

    setAlertConfig({
      type: 'warning',
      text: `${instance.disabled ? 'Desbloquear' : 'Bloquear'} instância ${instance.name}?`,
      withoutConfirm: true,
      withFunction: true,
      confirmFunction: handleBlock,
    });
  };

  const handleDeleteInstance = async (instance) => {
    const handleDelete = async () => {
      const res = await instanceAPI.deleteInstance(instance.id);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: `Instância ${instance.name} apagada com sucesso!`,
      });
    };

    setAlertConfig({
      type: 'warning',
      text: `Apagar a instância ${instance.name}?`,
      withoutConfirm: true,
      withFunction: true,
      confirmFunction: handleDelete,
    });
  };

  /**
   * Handles removal of a report
   * @param {object} report report info
   */
  const handleDeleteReport = async (report) => {
    const handleDelete = async () => {
      const res = await instanceAPI.deleteReport(report.id);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }
      setAlertConfig({
        type: 'success',
        text: `Relatório ${report.filename} apagado com sucesso!`,
      });
    };

    setAlertConfig({
      type: 'warning',
      text: `Tem certeza que deseja apagar o relatório ${report.filename}?`,
      withoutConfirm: true,
      withFunction: true,
      confirmFunction: handleDelete,
    });
  };

  /**
   * Opens an alert showing the report's update information
   * @param {object} report report info
   */
  const openReportStatus = async (report, fetchUpdate) => {
    /**
     * Updates a file manually
     * @param {object?} file File object (undefined for 'LegalOne' reports)
     */
    const handleUpdate = async (file) => {
      const res = await instanceAPI.manualReportUpdate(report.id, report.origin, file, report.filename);
      if (res.error) {
        setAlertConfig({
          type: res.type === 'warning' ? 'warning' : 'error',
          text: res.type === 'warning' ? `Relatório ${report.filename} atualizado!` : res.msg,
          child: res.raw,
        });
        return;
      }
      setAlertConfig({
        type: res.type,
        text: res.type === 'success'
          ? `Relatório ${report.filename} atualizado com sucesso!`
          : `Relatório ${report.filename} atualizado parcialmente!`,
        child: (res.type === 'warning' && res.data.errors && res.data.errors[0].substring(0, 3) === 'KPI')
          ? `${res.data.errors.length} KPI's não puderam ser atualizados. Houve algum erro na tentativa de construir esses KPI's!`
          : '',
      });
      fetchUpdate();
    };

    /**
     * Handles a file upload for the update
     * @param {object[]?} files File object (undefined for 'LegalOne' reports)
     */
    const updateFileUp = (files = []) => {
      if (report.origin === 'FileApi') {
        const errorMsg = verifyFileSheetType(files[0]);
        if (errorMsg.length > 0) {
          setAlertConfig({
            type: 'error',
            text: 'Erro na atualização!',
            child: errorMsg,
          });
          return;
        }
      }
      setAlertConfig({
        type: 'warning',
        text: `Atualizar o relatório ${report.filename} manualmente?`,
        withoutConfirm: true,
        withFunction: true,
        confirmFunction: () => handleUpdate(files[0]),
      });
    };

    const res = await instanceAPI.getReportStatus(report.id);
    if (res.error) {
      setAlertConfig({
        type: 'error',
        text: res.msg,
        child: res.raw,
      });
      return;
    }

    const infoReport = {
      ...res.data,
    };

    let status = '';
    let msg = '';
    if (infoReport.lastAttemptStatus === 'ERROR') {
      status = 'error';
      msg = 'Erro ao atualizar o relatório!';
    } else if (infoReport.lastAttemptStatus === 'WARNING') {
      status = 'warning';
      msg = 'Relatório atualizado!';
    } else {
      status = 'success';
      msg = 'Relatório atualizado com sucesso!';
    }
    setAlertConfig({
      type: status,
      text: msg,
      child: <FileReport file={infoReport} manualUpdate={updateFileUp} />,
    });
  };

  useEffect(() => {
    (async () => {
      if (isMounted.current) return;
      instanceAPI.getInstances();
    })();
  }, [instanceAPI]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ### Função para um usuário isAdmin = true adicionar um usuário em uma instância de cliente
  const handleAddUser = async (instance) => {
    const users = await handleGetInstanceUser(instance);

    const res = {
      supers: [],
      scientists: [],
      guests: [],
      supersQtd: 0,
      scientistsQtd: 0,
      guestsQtd: 0,
    };

    const getInfo = (item) => {
      switch (item.role) {
        case 'super':
          res.supers.push({ id: item.uid, label: item.email, value: item.email });
          res.supersQtd += 1;
          break;
        case 'scientist':
          res.scientists.push({ id: item.uid, label: item.email, value: item.email });
          res.scientistsQtd += 1;
          break;
        case 'guest':
          res.guests.push({ id: item.uid, label: item.email, value: item.email });
          res.guestsQtd += 1;
          break;
        default:
          break;
      }
    };

    users.forEach((u) => getInfo(u));

    const checkLimit = (role) => {
      const { supersQtd, scientistsQtd, guestsQtd } = res;
      switch (role) {
        case 'super':
          if (supersQtd >= instance.superS) {
            setAlertConfig({
              type: 'error',
              text: 'Limite máximo de usuários proprietários atingido nesta instância',
            });
            return false;
          }
          break;
        case 'scientist':
          if (scientistsQtd >= instance.scientists) {
            setAlertConfig({
              type: 'error',
              text: 'Limite máximo usuários cientistas atingido nesta instância',
            });
            return false;
          }
          break;
        case 'guest':
          if (guestsQtd >= instance.guests) {
            setAlertConfig({
              type: 'error',
              text: 'Limite máximo de usuários convidados atingido nesta instância',
            });
            return false;
          }
          break;
        default:
          break;
      }
      return true;
    };

    const submitAddUser = async (userInfo) => {
      if (!checkLimit(userInfo.role)) return;
      const { error, msg, raw } = await instanceAPI.addUser(
        instance.id, userInfo.scientist, userInfo.email, userInfo.role,
      );

      if (error) {
        setAlertConfig({
          type: 'error',
          text: msg,
          child: raw,
        });
        instanceAPI.getInstanceUsers(instance.id);
        toggleModal();
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Usuário adicionado com sucesso!',
        okFunction: () => {
          instanceAPI.getInstanceUsers(instance.id);
          toggleModal();
        },
      });
    };

    setModalConfig({
      title: 'Adicionar Novo Usuário',
      className: 'newUser_modal',
      children: (
        <AddUser
          user={user}
          userIsAdmin={claimsUser?.is_admin}
          usersEmails={[
            ...res.supers.map((u) => u.value),
            ...res.scientists.map((u) => u.value),
            ...res.guests.map((u) => u.value),
          ]}
          scientistEmails={[...res.supers, ...res.scientists]}
          hide={() => {
            toggleModal();
          }}
          fromAdmin
          submitData={submitAddUser}
        />
      ),
    });
  };

  const handleAddAntecipei = async (instance) => {
    const resGetUsers = await instanceAPI.getInstanceUsers(instance.id);
    if (resGetUsers.error) {
      setAlertConfig({
        type: 'error',
        text: resGetUsers.msg,
        child: resGetUsers.raw,
      });
      toggleModal();
      return;
    }
    const users = resGetUsers.users.filter((u) => u.role !== 'guest');

    const submitAntecipei = async (
      filename,
      ownerId,
      url,
      sendWithoutDataProc,
      prettyColumns,
      reportToken,
    ) => {
      const { error, msg, raw } = await instanceAPI.addAntecipeiReport(
        filename,
        ownerId,
        instance.id,
        url,
        sendWithoutDataProc,
        prettyColumns,
        reportToken,
      );

      if (error) {
        setAlertConfig({
          type: 'error',
          text: msg,
          child: raw,
        });
        toggleModal();
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Relatório Antecipei criado com sucesso!',
        okFunction: () => {
          toggleModal();
        },
      });
    };

    setModalConfig({
      title: 'Criar Relatório Antecipei',
      children: (
        <AddAntecipei
          users={users.map((u) => ({
            id: u.uid,
            label: u.email,
            value: u.email,
          }))}
          hide={() => {
            toggleModal();
          }}
          submitData={submitAntecipei}
        />
      ),
    });
  };

  const handleAddBenner = async (instance) => {
    const resGetUsers = await instanceAPI.getInstanceUsers(instance.id);
    if (resGetUsers.error) {
      setAlertConfig({
        type: 'error',
        text: resGetUsers.msg,
        child: resGetUsers.raw,
      });
      toggleModal();
      return;
    }
    const users = resGetUsers.users.filter((u) => u.role !== 'guest');

    const submitBenner = async (
      filename,
      ownerId,
      url,
      username,
      password,
    ) => {
      const { error, msg, raw } = await instanceAPI.addBennerReport(
        filename,
        ownerId,
        instance.id,
        url,
        username,
        password,
      );

      if (error) {
        setAlertConfig({
          type: 'error',
          text: msg,
          child: raw,
        });
        toggleModal();
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Relatório Benner criado com sucesso!',
        okFunction: () => {
          toggleModal();
        },
      });
    };

    setModalConfig({
      title: 'Criar Relatório Benner',
      children: (
        <AddBenner
          users={users.map((u) => ({
            id: u.uid,
            label: u.email,
            value: u.email,
          }))}
          hide={() => {
            toggleModal();
          }}
          submitData={submitBenner}
        />
      ),
    });
  };

  const handleOnboarding = async (instance) => {
    const resGetUsers = await instanceAPI.getInstanceUsers(instance.id);
    if (resGetUsers.error) {
      setAlertConfig({
        type: 'error',
        text: resGetUsers.msg,
        child: resGetUsers.raw,
      });
      toggleModal();
      return;
    }
    const users = resGetUsers.users.filter((u) => u.role !== 'guest');

    const submitOnbardingReport = async (filename, ownerId) => {
      const { error, msg, raw } = await instanceAPI.createOnboardingReport(
        filename, instance.id, ownerId,
      );

      if (error) {
        setAlertConfig({
          type: 'error',
          text: msg,
          child: raw,
        });
        toggleModal();
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Relatório de onboarding adicionado com sucesso!',
        okFunction: () => {
          filesAPI.getFilesList();
          toggleModal();
        },
      });
    };

    setModalConfig({
      title: 'Criar Relatório de Onboarding',
      children: (
        <OnboardingReport
          users={users.map((u) => ({
            id: u.uid,
            label: u.email,
            value: u.email,
          }))}
          hide={() => toggleModal()}
          submitData={submitOnbardingReport}
        />
      ),
    });
  };

  const handleRequestApiCredentials = async (instance) => {
    const { error, msg, raw } = await instanceAPI.requestApiCredentials(
      instance.id,
    );

    if (error) {
      setAlertConfig({
        type: 'error',
        text: msg,
        child: raw,
      });
      toggleModal();
      return;
    }

    setAlertConfig({
      type: 'success',
      text: 'Requisição enviada com sucesso!',
      child: 'O e-mail com as requisições foi enviado. Aguarde o cadastro das credenciais para que você possa utilizar a API!',
      okFunction: () => {
        toggleModal();
      },
    });
  };

  return (
    <>
      {instanceState.isLoading && <Loader />}
      <AdminToolbar
        tab={tab}
        setTab={setTab}
        handleNewInstance={handleNewInstance}
        setFilterText={handleText}
      />
      {tab === 'customers' && (
        <InstanceTable
          instances={instanceState.instances}
          openGuestsModal={openGuestsModal}
          openSupersModal={openSupersModal}
          openScientistsModal={openScientistModal}
          //
          handleEdit={handleEditInstance}
          handleBlock={handleBlockInstance}
          handleDelete={handleDeleteInstance}
          handleAddUser={handleAddUser}
          handleAddAntecipei={handleAddAntecipei}
          handleAddBenner={handleAddBenner}
          handleOnboarding={handleOnboarding}
          handleRequestApiCredentials={handleRequestApiCredentials}
          //
          filterText={filterText}
        />
      )}
      {tab === 'reports' && (
        <ReportsTable
          reports={instanceState.reports}
          instances={instanceState.instances}
          //
          getReports={instanceAPI.getReports}
          deleteReport={handleDeleteReport}
          openReportStatus={openReportStatus}
        />
      )}
    </>
  );
}

/**
 * Fazendo esse HOC, ao invés de usar o InstanceProvider no App,
 * é possível deixar as funções do instanceProvider somente para a página de admin.
 */

const AdminHOC = ({ ...props }) => (
  <InstanceProvider>
    <AdminPage {...props} />
  </InstanceProvider>
);

export default AdminHOC;
